"use strict";
/**
 * @fileoverview 高级UI组件 - 表格、树形视图、图表等复杂组件
 * @description 提供高级UI组件的实现，包括数据表格、树形视图、图表等
 *
 * @author ESEngine Team
 * @version 2.1.0
 * @since 2.1.0
 * @license MIT
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ChartComponent = exports.TreeViewComponent = exports.TableComponent = void 0;
const BaseComponent_1 = require("./BaseComponent");
/**
 * 表格组件
 */
class TableComponent extends BaseComponent_1.BaseComponent {
    constructor(config) {
        super();
        this.config = config;
    }
    /**
     * 创建DOM元素
     */
    createElement() {
        this.element = document.createElement('div');
        this.element.className = 'nexus-table';
    }
    /**
     * 渲染组件
     */
    render() {
        this.createTable();
    }
    /**
     * 创建表格
     */
    createTable() {
        this.element.style.cssText = `
            border: 1px solid #e0e0e0;
            border-radius: 4px;
            overflow: hidden;
            background: white;
            font-family: system-ui, -apple-system, sans-serif;
            font-size: 14px;
        `;
        if (this.config.showHeader !== false) {
            this.createHeader();
        }
        this.createBody();
    }
    /**
     * 创建表头
     */
    createHeader() {
        const header = document.createElement('div');
        header.style.cssText = `
            display: flex;
            background: #f5f5f5;
            border-bottom: 1px solid #e0e0e0;
            font-weight: bold;
        `;
        this.config.columns.forEach(column => {
            const cell = document.createElement('div');
            cell.textContent = column.title;
            cell.style.cssText = `
                flex: ${column.width ? `0 0 ${column.width}px` : '1'};
                padding: 12px 16px;
                text-align: ${column.align || 'left'};
                border-right: 1px solid #e0e0e0;
            `;
            header.appendChild(cell);
        });
        this.element.appendChild(header);
    }
    /**
     * 创建表体
     */
    createBody() {
        const body = document.createElement('div');
        this.config.data.forEach((row, index) => {
            const rowElement = document.createElement('div');
            rowElement.style.cssText = `
                display: flex;
                cursor: pointer;
                ${this.config.bordered ? 'border-bottom: 1px solid #e0e0e0;' : ''}
            `;
            rowElement.addEventListener('click', () => {
                var _a, _b;
                (_b = (_a = this.config).onRowClick) === null || _b === void 0 ? void 0 : _b.call(_a, row, index);
            });
            this.config.columns.forEach(column => {
                var _a;
                const cell = document.createElement('div');
                cell.textContent = ((_a = row[column.key]) === null || _a === void 0 ? void 0 : _a.toString()) || '';
                cell.style.cssText = `
                    flex: ${column.width ? `0 0 ${column.width}px` : '1'};
                    padding: 12px 16px;
                    text-align: ${column.align || 'left'};
                    border-right: 1px solid #e0e0e0;
                `;
                rowElement.appendChild(cell);
            });
            body.appendChild(rowElement);
        });
        this.element.appendChild(body);
    }
}
exports.TableComponent = TableComponent;
/**
 * 树形视图组件
 */
class TreeViewComponent extends BaseComponent_1.BaseComponent {
    constructor(config) {
        super();
        this.config = config;
    }
    /**
     * 创建DOM元素
     */
    createElement() {
        this.element = document.createElement('div');
        this.element.className = 'nexus-tree-view';
    }
    /**
     * 渲染组件
     */
    render() {
        this.createTreeView();
    }
    /**
     * 创建树形视图
     */
    createTreeView() {
        this.element.style.cssText = `
            font-family: system-ui, -apple-system, sans-serif;
            font-size: 14px;
            user-select: none;
        `;
        this.config.data.forEach(node => {
            this.createNode(node, 0);
        });
    }
    /**
     * 创建节点
     */
    createNode(node, level) {
        const nodeElement = document.createElement('div');
        nodeElement.style.cssText = `
            margin-left: ${level * (this.config.indent || 20)}px;
            cursor: pointer;
            padding: 4px 8px;
            border-radius: 4px;
            display: flex;
            align-items: center;
        `;
        // 展开/折叠图标
        if (node.children && node.children.length > 0) {
            const expandIcon = document.createElement('span');
            expandIcon.textContent = node.expanded ? '▼' : '▶';
            expandIcon.style.cssText = `
                font-size: 12px;
                width: 16px;
                margin-right: 4px;
            `;
            nodeElement.appendChild(expandIcon);
        }
        // 节点标题
        const title = document.createElement('span');
        title.textContent = node.title;
        nodeElement.appendChild(title);
        // 添加事件
        nodeElement.addEventListener('click', () => {
            var _a, _b;
            (_b = (_a = this.config).onNodeClick) === null || _b === void 0 ? void 0 : _b.call(_a, node);
        });
        this.element.appendChild(nodeElement);
        // 添加子节点
        if (node.children && node.expanded) {
            node.children.forEach(child => {
                this.createNode(child, level + 1);
            });
        }
    }
}
exports.TreeViewComponent = TreeViewComponent;
/**
 * 简单图表组件
 */
class ChartComponent extends BaseComponent_1.BaseComponent {
    constructor(config) {
        super();
        this.config = config;
    }
    /**
     * 创建DOM元素
     */
    createElement() {
        this.element = document.createElement('div');
        this.element.className = 'nexus-chart';
    }
    /**
     * 渲染组件
     */
    render() {
        this.createChart();
    }
    /**
     * 创建图表
     */
    createChart() {
        this.element.style.cssText = `
            border: 1px solid #e0e0e0;
            border-radius: 4px;
            background: white;
            padding: 16px;
            text-align: center;
        `;
        if (this.config.title) {
            const title = document.createElement('h3');
            title.textContent = this.config.title;
            title.style.cssText = `
                margin: 0 0 16px 0;
                font-size: 16px;
                font-weight: bold;
            `;
            this.element.appendChild(title);
        }
        const canvas = document.createElement('canvas');
        canvas.width = this.config.width;
        canvas.height = this.config.height;
        canvas.style.cssText = `
            display: block;
            margin: 0 auto;
            border: 1px solid #ddd;
        `;
        // 这里可以实现具体的图表绘制逻辑
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.fillStyle = '#f0f0f0';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#333';
            ctx.font = '16px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(`${this.config.type} 图表`, canvas.width / 2, canvas.height / 2);
        }
        this.element.appendChild(canvas);
    }
}
exports.ChartComponent = ChartComponent;
